import React, { useEffect, useMemo, useState } from 'react';
import { Eye, EyeOff, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../ui/sheet';
import {
  FUNDING_RIGHT_DRAWER_CLASS,
} from './fundingDrawerUi';
import {
  ENTITY_STATUS_OPTIONS,
  ENTITY_TYPE_OPTIONS,
  OWNER_ROLE_OPTIONS,
  buildEntityFromForm,
  emptyEntityForm,
  emptyOwnerForm,
  ownershipTotal,
} from './fundingClientEntities';

const SecureInput = ({ value, onChange, placeholder, id }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="fg-secure-input-row">
      <input
        id={id}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete="off"
      />
      <button type="button" className="fg-inline-icon-btn" onClick={() => setVisible((v) => !v)}>
        {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        {visible ? 'Hide' : 'View'}
      </button>
    </div>
  );
};

const Field = ({ label, children, full }) => (
  <div className={`funding-client-field fg-form-field ${full ? 'full' : ''}`}>
    <label>{label}</label>
    {children}
  </div>
);

const AddFundingEntityDrawer = ({ open, onOpenChange, client, onSave }) => {
  const [form, setForm] = useState(() => emptyEntityForm(client));

  useEffect(() => {
    if (open) {
      setForm(emptyEntityForm(client));
    }
  }, [open, client]);

  const ownershipSum = useMemo(() => ownershipTotal(form.owners), [form.owners]);
  const ownershipWarning = ownershipSum > 0 && ownershipSum !== 100;

  const setEntity = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const setOwner = (index, key, value) => {
    setForm((prev) => {
      const owners = [...prev.owners];
      owners[index] = { ...owners[index], [key]: value };
      return { ...prev, owners };
    });
  };

  const addOwner = () => {
    setForm((prev) => ({
      ...prev,
      owners: [...prev.owners, { ...emptyOwnerForm(), isPrimary: false, role: 'Co-owner' }],
    }));
  };

  const removeOwner = (index) => {
    setForm((prev) => ({
      ...prev,
      owners: prev.owners.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.entityType?.trim()) {
      toast.error('Entity type is required');
      return;
    }
    if (!form.label?.trim()) {
      toast.error('Entity label is required');
      return;
    }
    const needsOwners = form.entityType !== 'Personal Profile';
    const namedOwners = form.owners.filter((o) => o.name?.trim());
    if (needsOwners && namedOwners.length === 0) {
      toast.error('Add at least one owner');
      return;
    }
    if (ownershipWarning) {
      toast.warning(`Ownership totals ${ownershipSum}% (expected 100%)`);
    }
    const entity = buildEntityFromForm(form, client);
    onSave?.(entity);
    onOpenChange(false);
    toast.success('Entity added');
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        hideOverlay
        className={`${FUNDING_RIGHT_DRAWER_CLASS} fg-manual-drawer w-full overflow-y-auto sm:max-w-2xl`}
        data-testid="add-funding-entity-drawer"
      >
        <SheetHeader>
          <SheetTitle>Add Entity</SheetTitle>
          <SheetDescription>
            Add a business or personal entity with owners for this funding client.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="funding-client-drawer__body mt-4 space-y-5 pb-28">
          <div className="funding-client-form-section fg-form-section">
            <div className="funding-client-form-section__heading fg-section-title">
              <span className="funding-client-form-section__number">1</span>
              <div>
                <h4 className="funding-client-form-section__title">Entity details</h4>
                <p className="funding-client-form-section__subtitle">Identity and operating information for this entity.</p>
              </div>
            </div>
            <div className="funding-client-form-section__grid fg-form-grid">
              <Field label="Entity label" full>
                <input value={form.label} onChange={setEntity('label')} placeholder="Primary, Business 2, DBA…" required />
              </Field>
              <Field label="Entity type">
                <select value={form.entityType} onChange={setEntity('entityType')} data-testid="add-entity-type">
                  {ENTITY_TYPE_OPTIONS.map((type) => (
                    <option key={type}>{type}</option>
                  ))}
                </select>
              </Field>
              <Field label="Entity status">
                <select value={form.status} onChange={setEntity('status')}>
                  {ENTITY_STATUS_OPTIONS.map((status) => (
                    <option key={status}>{status}</option>
                  ))}
                </select>
              </Field>
              <Field label="Business legal name" full>
                <input value={form.businessLegalName} onChange={setEntity('businessLegalName')} placeholder="MS Beauty Studio LLC" />
              </Field>
              <Field label="DBA name">
                <input value={form.dbaName} onChange={setEntity('dbaName')} placeholder="Optional DBA" />
              </Field>
              <Field label="EIN">
                <SecureInput id="add-entity-ein" value={form.ein} onChange={setEntity('ein')} placeholder="88-4821931" />
              </Field>
              <Field label="Business start date">
                <input type="date" value={form.businessStartDate} onChange={setEntity('businessStartDate')} />
              </Field>
              <Field label="Business address" full>
                <input value={form.businessAddress} onChange={setEntity('businessAddress')} placeholder="44 Market Plaza, Orlando, FL" />
              </Field>
            </div>
          </div>

          <div className="funding-client-form-section fg-form-section">
            <div className="funding-client-form-section__heading fg-section-title">
              <span className="funding-client-form-section__number">2</span>
              <div>
                <h4 className="funding-client-form-section__title">Owners</h4>
                <p className="funding-client-form-section__subtitle">
                  {form.entityType === 'Personal Profile'
                    ? 'Personal Profile can use the client as the primary owner if none are added.'
                    : 'Add one or more owners for this entity.'}
                </p>
              </div>
            </div>

            {ownershipWarning ? (
              <div className="fg-hint-box fg-info-panel mb-3" data-testid="ownership-total-warning">
                <strong>Ownership total: {ownershipSum}%</strong>
                <p>Ownership should total 100% before submitting applications.</p>
              </div>
            ) : null}

            <div className="space-y-4">
              {form.owners.map((owner, index) => (
                <div key={`owner-${index}`} className="fg-owner-card" data-testid={`add-entity-owner-${index}`}>
                  <div className="fg-owner-card__header">
                    <strong>Owner {index + 1}</strong>
                    {form.owners.length > 1 ? (
                      <button type="button" className="fg-inline-icon-btn" onClick={() => removeOwner(index)} aria-label={`Remove owner ${index + 1}`}>
                        <Trash2 className="size-4" />
                      </button>
                    ) : null}
                  </div>
                  <div className="funding-client-form-section__grid fg-form-grid">
                    <Field label="Owner name">
                      <input
                        value={owner.name}
                        onChange={(e) => setOwner(index, 'name', e.target.value)}
                        placeholder="Marie Samuelle"
                      />
                    </Field>
                    <Field label="Role">
                      <select value={owner.role} onChange={(e) => setOwner(index, 'role', e.target.value)}>
                        {OWNER_ROLE_OPTIONS.map((role) => (
                          <option key={role}>{role}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Ownership %">
                      <input
                        value={owner.ownershipPercent}
                        onChange={(e) => setOwner(index, 'ownershipPercent', e.target.value)}
                        placeholder="100"
                      />
                    </Field>
                    <Field label="SSN">
                      <SecureInput
                        id={`add-entity-ssn-${index}`}
                        value={owner.ssn}
                        onChange={(e) => setOwner(index, 'ssn', e.target.value)}
                        placeholder="123-45-6789"
                      />
                    </Field>
                    <Field label="DOB">
                      <input type="date" value={owner.dob} onChange={(e) => setOwner(index, 'dob', e.target.value)} />
                    </Field>
                    <Field label="Employer">
                      <input value={owner.employer} onChange={(e) => setOwner(index, 'employer', e.target.value)} placeholder="Self-employed" />
                    </Field>
                    <Field label="Income">
                      <input value={owner.income} onChange={(e) => setOwner(index, 'income', e.target.value)} placeholder="$92,000" />
                    </Field>
                    <Field label="Personal address" full>
                      <input value={owner.personalAddress} onChange={(e) => setOwner(index, 'personalAddress', e.target.value)} placeholder="211 Palm Ridge Dr, Orlando, FL" />
                    </Field>
                    <Field label="Primary owner">
                      <label className="fg-checkbox-row">
                        <input
                          type="checkbox"
                          checked={Boolean(owner.isPrimary)}
                          onChange={(e) => setOwner(index, 'isPrimary', e.target.checked)}
                        />
                        <span>Set as primary owner</span>
                      </label>
                    </Field>
                  </div>
                </div>
              ))}
            </div>

            <button type="button" className="fg-btn fg-btn-soft mt-3" onClick={addOwner} data-testid="add-entity-owner-btn">
              <Plus className="mr-2 inline size-4" />
              Add owner
            </button>
          </div>

          <div className="funding-client-drawer__footer fg-drawer-footer">
            <button type="button" className="fg-btn fg-btn-light" onClick={() => onOpenChange(false)}>Cancel</button>
            <button type="submit" className="fg-btn fg-btn-primary" data-testid="add-entity-submit">Save entity</button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default AddFundingEntityDrawer;
